/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback } from 'react';

import { fetchAPI } from '@/api/fetch';
import { apiGetJobCandidateDetails, apiGetUnscheduledCandidates } from '@/api/services';
import { useCreateInterviewAgent } from '@/store/createInterviewAgent';
import { Client } from '@/types/Client';
import { CandidateWithJobContext } from '@/types/Candidate';

export const useSessionData = () => {
  const [isLoadingCandidate, setIsLoadingCandidate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { clients, setClients, setJobs, setCandidates, isUpdateMode } = useCreateInterviewAgent();

  const handleError = useCallback((message: string) => {
    setError(message);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch clients on component mount (create mode only)
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setIsLoadingCandidate(true);
        const response = await fetchAPI<Client[]>({
          request: () => apiGetUnscheduledCandidates()
        });

        if (response.success) {
          setClients(response.data);
        } else {
          console.error('Error fetching clients:', response.message);
          handleError('Failed to fetch clients. Please try again later.');
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        handleError('Failed to fetch clients. Please try again later.');
      } finally {
        setIsLoadingCandidate(false);
      }
    };

    if (!isUpdateMode) {
      fetchClients();
    }
  }, [isUpdateMode, setClients, handleError]);

  // Get candidate follow-up questions
  const getCandidateFollowUpQuestions = useCallback(async (jobId: string, candidateId: string) => {
    try {
      const response = await fetchAPI<CandidateWithJobContext>({
        request: () =>
          apiGetJobCandidateDetails({
            jobId,
            candidateId
          })
      });

      if (response.success) {
        return response.data.alerts_and_follow_up_questions || [];
      } else {
        throw new Error(`Failed to fetch candidate details: ${response.message}`);
      }
    } catch (error) {
      console.error('Error fetching candidate follow-up questions:', error);
      throw error;
    }
  }, []);

  // Selection handlers
  const handleClientSelection = useCallback(
    (selectedClient?: Client, setValue?: any) => {
      setJobs(selectedClient?.jobs || []);
      setValue?.('job', undefined);
      setValue?.('candidate', undefined);
    },
    [setJobs]
  );

  const handleJobSelection = useCallback(
    (selectedJob?: any, setValue?: any) => {
      setCandidates(selectedJob?.candidates || []);
      setValue?.('candidate', undefined);
    },
    [setCandidates]
  );

  return {
    isLoadingCandidate,
    error,
    handleError,
    clearError,
    getCandidateFollowUpQuestions,
    handleClientSelection,
    handleJobSelection,
    noCandidates: !isLoadingCandidate && clients?.length === 0
  };
};
